import React from 'react';
import { WizardState, NisabStandard } from '../../../hooks/useZakatWizard';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { CheckCircle2, RefreshCw, Info } from 'lucide-react';
import { motion } from 'framer-motion';
interface SummaryStepProps {
  state: WizardState;
  totals: {
    grossAssets: number;
    totalLiabilities: number;
    netAssets: number;
    goldNisabValue: number;
    silverNisabValue: number;
    currentNisabValue: number;
    zakatAmountGold: number;
    zakatAmountSilver: number;
    zakatAmount: number;
    isZakatDue: boolean;
    isZakatDueGold: boolean;
    isZakatDueSilver: boolean;
  };
  constants: {
    GOLD_PRICE_USD_PER_GRAM: number;
    SILVER_PRICE_USD_PER_GRAM: number;
    GOLD_NISAB_GRAMS: number;
    SILVER_NISAB_GRAMS: number;
  };
  onNisabChange: (standard: NisabStandard) => void;
  onReset: () => void;
}
export function SummaryStep({
  state,
  totals,
  constants,
  onNisabChange,
  onReset
}: SummaryStepProps) {
  // Consistent 2 decimal places everywhere
  const formatCurrency = (val: number) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(val);
  const formatPrice = (val: number) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(val);
  return <div className="space-y-8" data-id="element-475">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="text-center mb-8" data-id="element-476">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-teal-100 text-[#2D9596] mb-4" data-id="element-477">
          <CheckCircle2 className="w-8 h-8" data-id="element-478" />
        </div>
        <h2 className="text-3xl font-bold text-gray-900 font-serif mb-2" data-id="element-479">
          Your Zakat Calculation
        </h2>
        {state.email && <p className="text-gray-600" data-id="element-480">
            Your report for <strong data-id="element-481">{state.email}</strong>
          </p>}
      </motion.div>

      {/* Current Prices */}
      <Card className="bg-gradient-to-r from-amber-50 to-gray-50 border-amber-100" data-id="element-482">
        <div className="flex items-center gap-2 mb-3" data-id="element-483">
          <Info className="w-4 h-4 text-amber-600" data-id="element-484" />
          <h4 className="text-sm font-semibold text-gray-700" data-id="element-485">
            Today's Market Prices
          </h4>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm" data-id="element-486">
          <div data-id="element-487">
            <div className="text-gray-500" data-id="element-488">Gold Price</div>
            <div className="font-semibold text-amber-600" data-id="element-489">
              {formatPrice(constants.GOLD_PRICE_USD_PER_GRAM)}/gram
            </div>
          </div>
          <div data-id="element-490">
            <div className="text-gray-500" data-id="element-491">Silver Price</div>
            <div className="font-semibold text-gray-600" data-id="element-492">
              {formatPrice(constants.SILVER_PRICE_USD_PER_GRAM)}/gram
            </div>
          </div>
          <div data-id="element-493">
            <div className="text-gray-500" data-id="element-494">
              Gold Nisab ({constants.GOLD_NISAB_GRAMS}g)
            </div>
            <div className="font-semibold text-gray-900" data-id="element-495">
              {formatCurrency(totals.goldNisabValue)}
            </div>
          </div>
          <div data-id="element-496">
            <div className="text-gray-500" data-id="element-497">
              Silver Nisab ({constants.SILVER_NISAB_GRAMS}g)
            </div>
            <div className="font-semibold text-gray-900" data-id="element-498">
              {formatCurrency(totals.silverNisabValue)}
            </div>
          </div>
        </div>
      </Card>

      {/* Nisab Standard Toggle */}
      <div data-id="element-499">
        <h3 className="text-sm font-semibold text-gray-700 mb-3" data-id="element-500">
          Choose Your Nisab (Threshold) Standard
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-501">
          <motion.button whileTap={{
          scale: 0.98
        }} onClick={() => onNisabChange('silver')} className={`p-5 rounded-xl border-2 text-left transition-all ${state.nisabStandard === 'silver' ? 'border-[#2D9596] bg-teal-50 shadow-sm' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-502">
            <div className="flex justify-between items-start mb-2" data-id="element-503">
              <div data-id="element-504">
                <div className="font-bold text-gray-900" data-id="element-505">Silver Standard</div>
                <div className="text-xs text-gray-500" data-id="element-506">
                  Strict / Recommended
                </div>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${state.nisabStandard === 'silver' ? 'border-[#2D9596] bg-[#2D9596]' : 'border-gray-300'}`} data-id="element-507">
                {state.nisabStandard === 'silver' && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-508">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-509" />
                  </svg>}
              </div>
            </div>
            <p className="text-xs text-gray-600 mb-3" data-id="element-510">
              Lower threshold ({formatCurrency(totals.silverNisabValue)}). Most
              scholars recommend this as it benefits more Zakat recipients.
            </p>
            <div className="pt-3 border-t border-gray-200" data-id="element-511">
              <div className="text-xs text-gray-500" data-id="element-512">Zakat Due</div>
              <div className={`text-2xl font-bold ${totals.isZakatDueSilver ? 'text-[#2D9596]' : 'text-gray-400'}`} data-id="element-513">
                {formatCurrency(totals.zakatAmountSilver)}
              </div>
            </div>
          </motion.button>

          <motion.button whileTap={{
          scale: 0.98
        }} onClick={() => onNisabChange('gold')} className={`p-5 rounded-xl border-2 text-left transition-all ${state.nisabStandard === 'gold' ? 'border-[#2D9596] bg-teal-50 shadow-sm' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-514">
            <div className="flex justify-between items-start mb-2" data-id="element-515">
              <div data-id="element-516">
                <div className="font-bold text-gray-900" data-id="element-517">Gold Standard</div>
                <div className="text-xs text-gray-500" data-id="element-518">
                  Lenient / Alternative
                </div>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${state.nisabStandard === 'gold' ? 'border-[#2D9596] bg-[#2D9596]' : 'border-gray-300'}`} data-id="element-519">
                {state.nisabStandard === 'gold' && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-520">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-521" />
                  </svg>}
              </div>
            </div>
            <p className="text-xs text-gray-600 mb-3" data-id="element-522">
              Higher threshold ({formatCurrency(totals.goldNisabValue)}). Some
              scholars permit this, especially for those with many dependents.
            </p>
            <div className="pt-3 border-t border-gray-200" data-id="element-523">
              <div className="text-xs text-gray-500" data-id="element-524">Zakat Due</div>
              <div className={`text-2xl font-bold ${totals.isZakatDueGold ? 'text-[#2D9596]' : 'text-gray-400'}`} data-id="element-525">
                {formatCurrency(totals.zakatAmountGold)}
              </div>
            </div>
          </motion.button>
        </div>
      </div>

      {/* Main Result */}
      <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      delay: 0.2
    }} className={`p-8 rounded-2xl text-center ${totals.isZakatDue ? 'bg-teal-50 border-2 border-teal-100' : 'bg-gray-50 border-2 border-gray-100'}`} data-id="element-526">
        {totals.isZakatDue ? <>
            <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-527">
              Your Zakat Obligation
            </h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto" data-id="element-528">
              Based on the{' '}
              {state.nisabStandard === 'silver' ? 'Silver' : 'Gold'} Nisab
              (threshold) of {formatCurrency(totals.currentNisabValue)}
            </p>
            <div className="text-5xl font-bold text-[#2D9596] font-serif mb-2" data-id="element-529">
              {formatCurrency(totals.zakatAmount)}
            </div>
            <p className="text-sm text-gray-500 uppercase tracking-wider font-semibold" data-id="element-530">
              2.5% of {formatCurrency(totals.netAssets)}
            </p>
          </> : <>
            <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-531">
              No Zakat Due
            </h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto" data-id="element-532">
              Your net zakatable assets ({formatCurrency(totals.netAssets)}) are
              below the {state.nisabStandard === 'silver' ? 'Silver' : 'Gold'}{' '}
              Nisab (threshold) of {formatCurrency(totals.currentNisabValue)}.
            </p>
            <div className="text-5xl font-bold text-gray-400 font-serif mb-2" data-id="element-533">
              $0.00
            </div>
          </>}
      </motion.div>

      {/* Calculation Breakdown */}
      <Card title="Calculation Breakdown" data-id="element-534">
        <div className="space-y-4" data-id="element-535">
          <div className="flex justify-between items-center py-2 border-b border-gray-50" data-id="element-536">
            <span className="text-gray-600" data-id="element-537">Total Assets</span>
            <span className="font-medium text-lg" data-id="element-538">
              {formatCurrency(totals.grossAssets)}
            </span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-gray-50 text-red-600" data-id="element-539">
            <span data-id="element-540">Minus Liabilities</span>
            <span className="font-medium text-lg" data-id="element-541">
              -{formatCurrency(totals.totalLiabilities)}
            </span>
          </div>
          <div className="flex justify-between items-center py-3 border-b border-gray-50 bg-gray-50 -mx-6 px-6" data-id="element-542">
            <span className="font-bold text-gray-900" data-id="element-543">
              Net Zakatable Wealth
            </span>
            <span className="font-bold text-xl text-gray-900" data-id="element-544">
              {formatCurrency(totals.netAssets)}
            </span>
          </div>
          <div className="flex justify-between items-center py-2 text-sm text-gray-500" data-id="element-545">
            <span data-id="element-546">
              Nisab Threshold (
              {state.nisabStandard === 'silver' ? 'Silver' : 'Gold'})
            </span>
            <span data-id="element-547">{formatCurrency(totals.currentNisabValue)}</span>
          </div>
          <div className="flex justify-between items-center py-2 text-[#2D9596] font-bold" data-id="element-548">
            <span data-id="element-549">Zakat Rate</span>
            <span data-id="element-550">× 2.5%</span>
          </div>
        </div>
      </Card>

      {/* Disclaimer & Actions */}
      <div className="bg-gray-50 rounded-lg p-6 text-sm text-gray-500 text-center" data-id="element-551">
        <p className="mb-4" data-id="element-552">
          This calculation is for guidance purposes. Please consult with a
          qualified Islamic scholar for specific rulings on your situation. May
          Allah accept your Zakat and purify your wealth.
        </p>
        <Button variant="outline" onClick={onReset} className="gap-2" data-id="element-553">
          <RefreshCw className="w-4 h-4" data-id="element-554" />
          Start New Calculation
        </Button>
      </div>
    </div>;
}